import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';

import { get } from 'utils/lodash';

import {
  DEFAULT_INT_ZERO,
  CREDIT_CARD_MAX_LENGTH,
  CREDIT_CARD_MIN_LENGTH,
  ACCOUNT_NUMBER_MIN_LENGTH,
  OTHER_BILL_ACCOUNT_MAX_LENGTH,
  OTHER_BILL_ACCOUNT_MIN_LENGTH,
  OTHER_BILL_ACCOUNT_WATER_MIN_LENGTH,
} from 'settings/constants/common';
import {
  FORMAT_NUMBER,
  FORMAT_ALPHANUMERIC,
  FORMAT_BENEFICIARY_NAME,
  FORMAT_PASSWORD_CHARACTER,
  FORMAT_FAVOURITE_NICKNAME,
  FORMAT_ALPHABET_WITH_SPACE,
  FORMAT_BENEFICIARY_MESSAGE,
  DEFAULT_MAX_TRANSFER_AMOUNT,
  DEFAULT_MIN_TRANSFER_AMOUNT,
  FAVOURITE_NICKNAME_MIN_LENGTH,
  FAVOURITE_NICKNAME_MAX_LENGTH,
  FORMAT_ALPHANUMERIC_WITHOUT_SPACE,
  FORMAT_ALPHANUMERIC_WITH_SPACE_DOT,
  MOBILE_NUMBER_WITH_PREFIX_MIN_LENGTH,
  MOBILE_NUMBER_WITH_PREFIX_MAX_LENGTH,
  PURCHASE_MOBILE_PDAM,
} from 'settings/constants/transaction';

import { validateGbiInputField } from './digital-wealth/form-validation';
import {
  isValidEmail,
  isLengthValid,
  isValidAccount,
  isValidMaxAmount,
  isValidMinAmount,
  isValidMobileNumber,
  isValidEWalletAccount,
} from './validation';

import {
  onlyNumber,
  formatName,
  alphaNumeric,
  formatAsCurrency,
  removeStringComma,
  alphaNumericSpace,
  alphabetWithSpace,
  isAlphabetWithSpaceOnly,
  alphaNumericSpaceDot,
  formatFavoriteNickname,
  removeAlphabetWithSpace,
  allowedPasswordCharacter,
  beneficiaryMessage,
} from './formatter';

// NOTE: Declare these because the handler consumes response code to display message
const INVALID_ACCOUNT = 104;

export const validResult = { isValid: true, messageId: '' };
const emptyResult = { isValid: false, messageId: 'transaction.transfer.errorMessage.required' };

export const validateInterbankInput = formInput => {
  const amount = get(formInput, 'transferAmount', '');
  const accountNumber = get(formInput, 'accountNumber', '');

  // Validate account at first
  if (!accountNumber || accountNumber.length < ACCOUNT_NUMBER_MIN_LENGTH) {
    return { isValidInput: false, errorCode: INVALID_ACCOUNT };
  }

  if (!amount) {
    return { isValidInput: false, errorCode: 'REQUIRED' };
  }

  return { isValidInput: true };
};

export const validateField = {
  transferAmount: value => (!value ? emptyResult : validResult),

  currency: value => (!value ? emptyResult : validResult),

  accountNumber: value => {
    if (!value) return emptyResult;

    return !isValidAccount(value) ? { isValid: false, messageId: 'transaction.transfer.errorMessage.accountNumber' } : validResult;
  },

  // only execute if flag===TRUE
  transferDate: (value, flag) => (flag && !value ? emptyResult : validResult),

  // only execute if flag===TRUE
  transferEndDate: (value, flag) => (flag && !value ? emptyResult : validResult),

  // extra validation which doesnt map to field's name
  duplicateAccountNumber: (value, value2) =>
    value === value2 ? { isValid: false, messageId: 'transaction.transfer.errorMessage.sameAccount' } : validResult,

  duplicateDate: (value, value2, flag) =>
    flag && (moment(value).isSame(value2, 'day') || moment(value2).isBefore(value, 'day'))
      ? { isValid: false, messageId: 'transaction.transfer.errorMessage.sameEndDate' }
      : validResult,

  default: () => validResult,
};

// this function can be re-used for all Transfer types
export const validateTransferDetail = data => {
  const { isRecurring, transferDate, transferEndDate, accountNumber, senderAccountNumber } = data;

  const fields = Object.keys(data);

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const value = data[field];
    const result = (validateField[field] || validateField.default)(value, isRecurring);

    if (!result.isValid) {
      return result;
    }

    // if oneTime fieldValidation passed, check for recurring fields
    if (isRecurring && field === 'transferEndDate') {
      const recurringResult = validateField.duplicateDate(transferDate, transferEndDate, isRecurring);
      if (!recurringResult.isValid) {
        return recurringResult;
      }
    }
  }

  // final check, make sure account number is not duplicate
  return validateField.duplicateAccountNumber(accountNumber, senderAccountNumber);
};

export const validateAmountInput = ({
  amount,
  accountBalance,
  isSameCurrency = false,
  minAmount = DEFAULT_MIN_TRANSFER_AMOUNT,
  maxAmount = DEFAULT_MAX_TRANSFER_AMOUNT,
}) => {
  const valid = { isValid: true, errorMessage: '', param: {} };

  const plainAmount = removeStringComma(amount);

  // if API returns 0, use Default value
  const minLimit = minAmount > DEFAULT_INT_ZERO ? minAmount : DEFAULT_MIN_TRANSFER_AMOUNT;
  if (!isValidMinAmount(plainAmount, minLimit)) {
    return {
      isValid: false,
      errorMessage: { id: 'transaction.transfer.errorMessage.minAmount' },
      param: { amount: formatAsCurrency(String(minLimit)) },
    };
  }

  // if API returns 0 or less than minAmount, use Default value
  const maxLimit = maxAmount > DEFAULT_MIN_TRANSFER_AMOUNT ? maxAmount : DEFAULT_MAX_TRANSFER_AMOUNT;
  if (!isValidMaxAmount(plainAmount, maxLimit)) {
    return {
      isValid: false,
      errorMessage: { id: 'transaction.transfer.errorMessage.maxAmount' },
      param: { amount: formatAsCurrency(String(maxLimit)) },
    };
  }

  if (isSameCurrency && !isValidMaxAmount(plainAmount, accountBalance)) {
    return {
      isValid: false,
      errorMessage: { id: 'transaction.transfer.errorMessage.exceedBalance' },
      param: '',
    };
  }

  return valid;
};

/* Dynamic fields validation */
const validateAmount = (amount, { type, value, isValidateRequired }) => {
  // return if validate not required
  if (!isValidateRequired) {
    return validResult;
  }
  // remove comma
  // remove NumberField prefix that is included in value when using event.target.value
  const plainValue = removeAlphabetWithSpace(removeStringComma(amount)); // IDR 1,234.00 -> 1234.00

  const amountRules = {
    // check empty input
    required: () => (!isEmpty(plainValue) ? validResult : emptyResult),
    // check min amount allowed
    minAmount: () =>
      isValidMinAmount(plainValue, value)
        ? validResult
        : { isValid: false, messageId: 'transaction.transfer.errorMessage.minAmount', param: { amount: formatAsCurrency(String(value)) } },
    // check max amount allowed
    maxAmount: () =>
      isValidMaxAmount(plainValue, value)
        ? validResult
        : { isValid: false, messageId: 'transaction.transfer.errorMessage.maxAmount', param: { amount: formatAsCurrency(String(value)) } },
    // validate against balance
    senderBalance: () =>
      isValidMaxAmount(plainValue, value) ? validResult : { isValid: false, messageId: 'transaction.transfer.errorMessage.exceedBalance' },
    // default
    default: () => validResult,
  };

  return (amountRules[type] || amountRules.default)();
};

const validateTDAmount = (amount, { type, value, message, isValidateRequired }) => {
  // return if validate not required
  if (!isValidateRequired) {
    return validResult;
  }
  // remove comma
  // remove NumberField prefix that is included in value when using event.target.value
  const plainValue = removeAlphabetWithSpace(removeStringComma(amount)); // IDR 1,234.00 -> 1234.00

  const amountRules = {
    // check empty input
    required: () => (!isEmpty(plainValue) ? validResult : emptyResult),
    // check min amount allowed
    minAmount: () =>
      isValidMinAmount(plainValue, value)
        ? validResult
        : { isValid: false, messageId: message, param: { amount: formatAsCurrency(String(value)) } },
    // check max amount allowed
    maxAmount: () =>
      isValidMaxAmount(plainValue, value)
        ? validResult
        : { isValid: false, messageId: message, param: { amount: formatAsCurrency(String(value)) } },
    // validate against balance
    senderBalance: () =>
      isValidMaxAmount(plainValue, value) ? validResult : { isValid: false, messageId: 'transaction.transfer.errorMessage.exceedBalance' },
    // default
    default: () => validResult,
  };

  return (amountRules[type] || amountRules.default)();
};

const validateAccountNumber = (accountNumber, { type, isValidateRequired }) => {
  // return if validate is not required
  if (!isValidateRequired) {
    return validResult;
  }

  const transferSwift = document.getElementById('title-modal').innerHTML;

  const accountNumberRules = {
    // null value check
    required: () => (accountNumber ? validResult : emptyResult),
    // account no length validation
    accountLength: () =>
      isValidAccount(accountNumber, transferSwift)
        ? validResult
        : { isValid: false, messageId: 'transaction.transfer.errorMessage.accountNumber' },
    customerNumberLength: () =>
      isValidEWalletAccount(accountNumber)
        ? validResult
        : { isValid: false, messageId: 'transaction.transfer.errorMessage.customerNumber' },
    // IBAN number for swift transfer
    // validIBAN: () =>
    //   isValidIBAN(accountNumber) ? validResult : { isValid: false, messageId: 'transaction.transfer.errorMessage.invalidIban' },
    // default
    default: () => validResult,
  };

  return (accountNumberRules[type] || accountNumberRules.default)();
};

const validateDate = (date, { type, isValidateRequired }, referenceDate) => {
  // return if validate is not required
  if (!isValidateRequired) {
    return validResult;
  }

  const dateRule = {
    // null value check
    required: () => (date ? validResult : emptyResult),
    // date can't be equal or before to referenceDate
    duplicateDate: () => validateField.duplicateDate(referenceDate, date, true),
    // default
    default: () => validResult,
  };

  return (dateRule[type] || dateRule.default)();
};

const validateEmail = (email, { type, isValidateRequired }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  if (!email) {
    return validResult;
  }

  const emailRule = {
    validEmail: () =>
      isValidEmail(email) ? validResult : { isValid: false, messageId: 'transaction.transfer.errorMessage.beneficiaryEmail' },
    default: () => validResult,
  };

  return (emailRule[type] || emailRule.default)();
};

const validateMobileNumber = (number, { type, isValidateRequired }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const mobileRule = {
    required: () => (number ? validResult : emptyResult),
    validMobileNumber: () =>
      isValidMobileNumber(number) ? validResult : { isValid: false, messageId: 'transaction.payment.errorMessage.invalidMobileNumber' },
    validMobileNumberWithPrefix: () =>
      isValidMobileNumber(number, MOBILE_NUMBER_WITH_PREFIX_MIN_LENGTH, MOBILE_NUMBER_WITH_PREFIX_MAX_LENGTH)
        ? validResult
        : { isValid: false, messageId: 'transaction.payment.errorMessage.invalidMobileNumber' },
    default: () => validResult,
  };

  return (mobileRule[type] || mobileRule.default)();
};

const validateCustomerNumber = (
  number,
  {
    type,
    selectedBiller,
    isValidateRequired,
    minLength = OTHER_BILL_ACCOUNT_MIN_LENGTH,
    maxLength = OTHER_BILL_ACCOUNT_MAX_LENGTH,
    message,
  }
) => {
  const valueAutoComplete = document.getElementsByClassName(
    'MuiInputBase-input MuiInput-input MuiAutocomplete-input MuiAutocomplete-inputFocused MuiInputBase-inputAdornedEnd'
  )[0];

  if (!isValidateRequired) {
    return validResult;
  }

  if (!isEmpty(valueAutoComplete)) {
    // eslint-disable-next-line no-param-reassign
    minLength =
      !isEmpty(valueAutoComplete.value) && valueAutoComplete.value === PURCHASE_MOBILE_PDAM
        ? OTHER_BILL_ACCOUNT_WATER_MIN_LENGTH
        : minLength;
  }

  const messageId = !isEmpty(message) ? message : 'transaction.payment.errorMessage.invalidCustomerNumber';

  const rule = {
    required: () => (number ? validResult : emptyResult),
    validCustomerNumber: () => (isLengthValid(number, minLength, maxLength) ? validResult : { isValid: false, messageId }),
    default: () => validResult,
  };

  return (rule[type] || rule.default)();
};

const validatePaymentCode = (number, { type, isValidateRequired, minLength, maxLength }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (number ? validResult : emptyResult),
    validPaymentCode: () =>
      isLengthValid(number, minLength, maxLength)
        ? validResult
        : { isValid: false, messageId: 'transaction.purchase.errorMessage.invalidPaymentCode' },
    default: () => validResult,
  };
  return (rule[type] || rule.default)();
};

const validateCreditCardNumber = (number, { type, isValidateRequired }) => {
  const plainNumber = onlyNumber(number);

  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (plainNumber ? validResult : emptyResult),

    validCcNumber: () =>
      isLengthValid(plainNumber, CREDIT_CARD_MIN_LENGTH, CREDIT_CARD_MAX_LENGTH)
        ? validResult
        : { isValid: false, messageId: 'transaction.payment.errorMessage.invalidCreditCardNumber' },
    default: () => validResult,
  };

  return (rule[type] || rule.default)();
};

const validateReferenceNumber = (number, { type, isValidateRequired, minLength, maxLength }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (number ? validResult : emptyResult),
    validReferenceNumber: () =>
      isLengthValid(number, minLength, maxLength)
        ? validResult
        : { isValid: false, messageId: 'transaction.payment.errorMessage.invalidCustomerNumber' },
    default: () => validResult,
  };
  return (rule[type] || rule.default)();
};

const validateReferral = (number, { type, isValidateRequired, minLength, maxLength }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (number ? validResult : emptyResult),
    validPromoCode: () =>
      isLengthValid(number, minLength, maxLength)
        ? validResult
        : { isValid: false, messageId: 'dashboard.td.errorMessage.invalidPromoCode' },
    validReferralCode: () =>
      isLengthValid(number, minLength, maxLength)
        ? validResult
        : { isValid: false, messageId: 'dashboard.td.errorMessage.invalidReferralCode' },
    default: () => validResult,
  };
  return (rule[type] || rule.default)();
};

const validateOnlyRequired = (value, { isValidateRequired }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  return !isEmpty(value) ? validResult : emptyResult;
};

const validateRequiredWithTrim = (value, validationRule) => {
  const trimmedValue = value && typeof value === 'string' ? value.trim() : value;

  return validateOnlyRequired(trimmedValue, validationRule);
};

const validatePasswordMatch = (value, { type, isValidateRequired, password }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (value ? validResult : emptyResult),

    validPasswordMatch: () =>
      value === password ? validResult : { isValid: false, messageId: 'settings.errorMessage.confirmationNotMatch' },
    default: () => validResult,
  };

  return (rule[type] || rule.default)();
};

const validateAccountNickname = (value, { type, isValidateRequired = true }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (value ? validResult : emptyResult),
    validNickname: () =>
      isLengthValid(value, FAVOURITE_NICKNAME_MIN_LENGTH, FAVOURITE_NICKNAME_MAX_LENGTH)
        ? validResult
        : { isValid: false, messageId: 'transaction.favourite.errorMessage.invalidAccountNickname' },

    default: () => validResult,
  };

  return (rule[type] || rule.default)();
};

const validateBeneficiaryName = (beneficiaryName, { type, isValidateRequired }) => {
  if (!isValidateRequired) {
    return validResult;
  }

  const rule = {
    required: () => (beneficiaryName && beneficiaryName.trim() !== '' ? validResult : emptyResult),

    validBeneficiaryName: () =>
      isAlphabetWithSpaceOnly(beneficiaryName)
        ? validResult
        : { isValid: false, messageId: 'transaction.transfer.sknTransfer.modalBeneficiaryName.message' },
    default: () => validResult,
  };

  return (rule[type] || rule.default)();
};

// validator object literal. Based from validateField object
const validateInputField = {
  ...validateGbiInputField,
  transferAmount: (value, validationRule) => validateAmount(value, validationRule),
  accountNumber: (value, validationRule) => validateAccountNumber(value, validationRule),
  transferEndDate: (value, validationRule, referenceDate) => validateDate(value, validationRule, referenceDate),
  beneficiaryName: (value, validationRule) => validateBeneficiaryName(value, validationRule),
  beneficiaryEmail: (value, validationRule) => validateEmail(value, validationRule),
  beneficiaryID: (value, validationRule) => validateOnlyRequired(value, validationRule),
  beneficiaryAddressStreet: (value, validationRule) => validateOnlyRequired(value, validationRule),
  currency: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldIdentityStatus: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldCitizenship: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldBeneficiary: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldRelationship: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldPurpose: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldDescription: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lldMessage: (value, validationRule) => validateOnlyRequired(value, validationRule),
  transactionCharges: (value, validationRule) => validateOnlyRequired(value, validationRule),
  lhbuTransactionPurpose: (value, validationRule) => validateOnlyRequired(value, validationRule),
  transactionDescription: (value, validationRule) => validateOnlyRequired(value, validationRule),
  underlyingDocumentType: (value, validationRule) => validateOnlyRequired(value, validationRule),
  beneficiaryDateOfBirth: (value, validationRule) => validateDate(value, validationRule),
  mobileNumber: (value, validationRule) => validateMobileNumber(value, validationRule),
  mobileNo: (value, validationRule) => validateMobileNumber(value, validationRule),
  customerNumber: (value, validationRule, referenceDate, selectedBillerWater) =>
    validateCustomerNumber(value, validationRule, referenceDate, selectedBillerWater),
  paymentOption: (value, validationRule) => validateOnlyRequired(value, validationRule),
  paymentAmount: (value, validationRule) => validateAmount(value, validationRule),
  creditCardNumber: (value, validationRule) => validateCreditCardNumber(value, validationRule),
  referenceNumber: (value, validationRule) => validateReferenceNumber(value, validationRule),
  paymentCode: (value, validationRule) => validatePaymentCode(value, validationRule),
  purchaseAmount: (value, validationRule) => validateAmount(value, validationRule),
  mobilePrefix: (value, validationRule) => validateOnlyRequired(value, validationRule),
  transferFrequency: (value, validationRule) => validateOnlyRequired(value, validationRule),
  displayName: (value, validationRule) => validateOnlyRequired(value, validationRule),
  accountNickname: (value, validationRule) => validateAccountNickname(value, validationRule),
  currencyCode: (value, validationRule) => validateOnlyRequired(value, validationRule),
  accountLimit: (value, validationRule) => validateOnlyRequired(value, validationRule),
  favouriteTransferType: (value, validationRule) => validateOnlyRequired(value, validationRule),
  toAccountNo: (value, validationRule) => validateAccountNumber(value, validationRule),
  emailAddress: (value, validationRule) => validateEmail(value, validationRule),
  address1: (value, validationRule) => validateOnlyRequired(value, validationRule),
  accountHolderName: (value, validationRule) => validateBeneficiaryName(value, validationRule),
  bankCode: (value, validationRule) => validateOnlyRequired(value, validationRule),
  favouritePaymentType: (value, validationRule) => validateOnlyRequired(value, validationRule),
  favouritePurchaseType: (value, validationRule) => validateOnlyRequired(value, validationRule),
  nickname: (value, validationRule) => validateAccountNickname(value, validationRule),
  billerType: (value, validationRule) => validateOnlyRequired(value, validationRule),
  payeeType: (value, validationRule) => validateOnlyRequired(value, validationRule),
  accountNo: (value, validationRule) => validateCustomerNumber(value, validationRule),
  payeeName: (value, validationRule) => validateOnlyRequired(value, validationRule),
  // Time Deposit
  fromAccount: (value, validationRule) => validateOnlyRequired(value, validationRule),
  term: (value, validationRule) => validateOnlyRequired(value, validationRule),
  amountDeposit: (value, validationRule) => validateTDAmount(value, validationRule),
  instructionAtMaturity: (value, validationRule) => validateOnlyRequired(value, validationRule),
  zakat: (value, validationRule) => validateOnlyRequired(value, validationRule),
  promoCodeValue: (value, validationRule) => validateReferral(value, validationRule),
  referralCodeValue: (value, validationRule) => validateReferral(value, validationRule),
  beneficiaryAddressCity: (value, validationRule) => validateRequiredWithTrim(value, validationRule),
  address3: (value, validationRule) => validateRequiredWithTrim(value, validationRule),
  default: () => validResult,
};

export const checkFormFormat = (value, format) => {
  const formatHandler = {
    [FORMAT_NUMBER]: () => onlyNumber(String(value)),
    [FORMAT_BENEFICIARY_NAME]: () => formatName(String(value)),
    [FORMAT_BENEFICIARY_MESSAGE]: () => beneficiaryMessage(String(value)),
    [FORMAT_ALPHANUMERIC]: () => alphaNumericSpace(String(value)),
    [FORMAT_ALPHANUMERIC_WITHOUT_SPACE]: () => alphaNumeric(String(value)),
    [FORMAT_ALPHANUMERIC_WITH_SPACE_DOT]: () => alphaNumericSpaceDot(String(value)),
    [FORMAT_ALPHABET_WITH_SPACE]: () => alphabetWithSpace(String(value)),
    [FORMAT_PASSWORD_CHARACTER]: () => allowedPasswordCharacter(String(value)),
    [FORMAT_FAVOURITE_NICKNAME]: () => formatFavoriteNickname(String(value)),
    default: () => value,
  };

  return (formatHandler[format] || formatHandler.default)();
};

export const validate = (validationRules, event, extraRule = {}) => {
  const { name, value } = event.target;
  const { selectedBiller } = extraRule;

  return validationRules.reduce((validationResult, validationRule) => {
    // eslint-disable-next-line no-param-reassign
    validationRule.selectedBiller = selectedBiller;

    // stop checking next rule if previous rule already failed
    if (!validationResult.isValid) return validationResult;
    const { isValid, messageId, param } = (validateInputField[name] || validateInputField.default)(value, validationRule, extraRule);
    if (!isValid) return { isValid, messageId, param };

    return validationResult;
  }, validResult);
};
